/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react-lite';
import { withTranslation } from 'react-i18next';

import NavDropdown from '../NavDropdown';
import contentLocaleStore from '../../stores/contentLocaleStore';

/**
 * Picks the Locale course content is shown in, and moves the site language -
 * translations and date formatting - to match. Both start from ?locale= or the
 * browser; the picker keeps them together from then on.
 *
 * Uses the local NavDropdown and holds its own open state, rather than
 * react-bootstrap's NavDropdown, which does not toggle under React 18 - the
 * same reason CategoryDropdown does it this way.
 */
const ContentLocalePicker = ({ iconOnly, t }) => {
  const { locales, contentLocale, setContentLocale } = contentLocaleStore;
  const [open, setOpen] = useState(false);
  const dropdownWrapper = useRef(null);

  const handleClickOutside = useCallback((event) => {
    if (
      dropdownWrapper.current &&
      !dropdownWrapper.current.contains(event.target)
    ) {
      setOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, false);

    return () => {
      document.removeEventListener('click', handleClickOutside, false);
    };
  }, [handleClickOutside]);

  // Nothing to pick between until the Locales load, and no point offering a
  // choice on instances that only support one Locale.
  if (!contentLocale || locales.length < 2) {
    return null;
  }

  return (
    <NavDropdown
      // The desktop and mobile navigations are both mounted (one is hidden by
      // CSS), so the two pickers need distinct DOM ids.
      id={
        iconOnly ? 'content-locale-dropdown-mobile' : 'content-locale-dropdown'
      }
      className="content-locale-picker"
      open={open}
      onClick={() => setOpen((previousOpen) => !previousOpen)}
      ref={dropdownWrapper}
      label={
        <span>
          <span className="sr-only">{t('weblink:language')}</span>
          <span
            className="glyphicon glyphicon-globe"
            style={{ marginRight: 3 }}
            aria-hidden="true"
          />
          {!iconOnly && (
            <>
              &nbsp;
              {contentLocale.name}
            </>
          )}
          &nbsp;
          <span className="caret" aria-hidden="true" />
        </span>
      }
    >
      {locales.map((locale) => (
        <li
          key={locale.id}
          className={locale.id === contentLocale.id ? 'active' : undefined}
        >
          <a
            role="menuitem"
            tabIndex={0}
            onClick={() => setContentLocale(locale)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                setContentLocale(locale);
              }
            }}
          >
            {locale.name}
          </a>
        </li>
      ))}
    </NavDropdown>
  );
};

ContentLocalePicker.defaultProps = {
  iconOnly: false,
};

ContentLocalePicker.propTypes = {
  iconOnly: PropTypes.bool,
  t: PropTypes.func.isRequired,
};

export default withTranslation()(observer(ContentLocalePicker));
